class Filter {
  constructor(container) {
    this.container = container;
    this.elements = this.cacheElements();
    this.appliedFilters = null;
    this.init();
  }

  cacheElements() {
    return {
      form: this.container.querySelector('.brand-venues__form'),
      cards: this.container.querySelectorAll('.brand-venues__card'),
      cardsContainer: this.container.querySelector('.brand-venues__cards'),
      quantityDisplay: this.container.querySelector('.brand-venues__quantity'),
      applyButton: this.container.querySelector('.brand-venues__filter-button'),
      resetButton: this.container.querySelector('.brand-venues__filter-link'),
      clearButton: this.container.querySelector('.brand-venues__action-close'),
      filtersSection: this.container.querySelector('.brand-venues__filters'),
    };
  }

  init() {
    if (!this.elements.form) return;

    // Submit event
    this.elements.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.applyFilters();
    });

    // Reset event
    this.elements.resetButton?.addEventListener('click', () => {
      setTimeout(() => {
        this.resetFilters();
      }, 0);
    });

    this.elements.clearButton?.addEventListener('click', () => {
      setTimeout(() => {
        this.restoreAppliedFilters();
      }, 800);
    });

    // Hide checkbox options that have zero matches on initial load
    this.hideUnusedCheckboxes();
  }

  applyFilters() {
    const formData = new FormData(this.elements.form);
    const filters = Filter.collectFilters(formData);

    this.appliedFilters = filters;

    let visibleCount = 0;

    this.elements.cards.forEach((card) => {
      const matches = Filter.matchesFilters(card, filters);
      if (matches) {
        Filter.showCard(card);
        visibleCount++;
      } else {
        Filter.hideCard(card);
      }
    });

    this.updateQuantity(visibleCount);
    this.toggleNoResults(visibleCount);
  }

  scrollToFilters() {
    if (!this.elements.filtersSection) return;

    const filtersRect = this.elements.filtersSection.getBoundingClientRect();
    const filtersSectionTop = filtersRect.top + window.pageYOffset;
    const offset = 100;
    const targetScroll = filtersSectionTop - offset;

    window.scrollTo({
      top: targetScroll,
      behavior: 'smooth',
    });

    setTimeout(() => {
      document.body.dataset.scrollY = window.pageYOffset;
    }, 500);
  }

  static showCard(card) {
    // eslint-disable-next-line no-param-reassign
    card.style.display = '';
    card.removeAttribute('hidden');
  }

  static hideCard(card) {
    // eslint-disable-next-line no-param-reassign
    card.style.display = 'none';
    card.setAttribute('hidden', '');
  }

  resetFilters() {
    this.appliedFilters = null;

    this.elements.cards.forEach((card) => {
      Filter.showCard(card);
    });

    this.updateQuantity(this.elements.cards.length);
    this.toggleNoResults(this.elements.cards.length);
  }

  static collectFilters(formData) {
    const filters = {
      number: {},
      range: {},
      checkbox: {},
    };

    // Process all form entries
    formData.forEach((value, key) => {
      // Handle number inputs with data-filter-field
      const NumberInput = document.querySelector(`input[type="number"][name="${key}"]`);
      if (NumberInput && NumberInput.dataset.filterField) {
        const field = NumberInput.dataset.filterField;
        if (value && value.trim() !== '') {
          filters.number[field] = parseInt(value, 10) || value;
        }
      }

      // Handle range inputs with data-filter-field
      const rangeInput = document.querySelector(`input[type="range"][name="${key}"]`);
      if (rangeInput && rangeInput.dataset.filterField) {
        const field = rangeInput.dataset.filterField;
        const { rangeType } = rangeInput.dataset; // 'min' or 'max'
        const inputMin = parseInt(rangeInput.getAttribute('min'), 10);
        const inputMax = parseInt(rangeInput.getAttribute('max'), 10);
        const inputValue = parseInt(value, 10);

        if (!filters.range[field]) {
          // eslint-disable-next-line max-len
          filters.range[field] = {
            min: null, max: null, originalMin: inputMin, originalMax: inputMax,
          };
        }

        if (rangeType === 'min' && inputValue !== inputMin) {
          filters.range[field].min = inputValue;
        } else if (rangeType === 'max' && inputValue !== inputMax) {
          filters.range[field].max = inputValue;
        }
      }
    });

    // Collect checkbox values by data-filter-field
    const checkboxGroups = document.querySelectorAll('[data-filter-type="checkbox"][data-filter-field]');
    checkboxGroups.forEach((group) => {
      const field = group.dataset.filterField;
      const checkboxes = group.querySelectorAll('input[type="checkbox"]:checked');
      const values = Array.from(checkboxes).map((cb) => cb.value);

      if (values.length > 0) {
        filters.checkbox[field] = values;
      }
    });

    return filters;
  }

  static matchesFilters(card, filters) {
    // eslint-disable-next-line no-restricted-syntax
    for (const [field, value] of Object.entries(filters.number)) {
      const fieldName = Filter.camelCase(field);
      const cardValue = parseInt(card.dataset[fieldName], 10);

      if (!cardValue || cardValue === 0) {
        return false;
      }

      if (cardValue < value) {
        return false;
      }
    }

    // Check range filters
    // eslint-disable-next-line no-restricted-syntax
    for (const [field, range] of Object.entries(filters.range)) {
      if (range.min === null && range.max === null) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const cardValue = parseInt(card.dataset[Filter.camelCase(field)], 10);

      if (!cardValue || cardValue === 0) {
        return false;
      }

      if (range.min !== null && cardValue < range.min) {
        return false;
      }
      if (range.max !== null && cardValue > range.max) {
        return false;
      }
    }

    // Check checkbox filters
    // eslint-disable-next-line no-restricted-syntax
    for (const [field, selectedValues] of Object.entries(filters.checkbox)) {
      const cardDataValue = card.dataset[Filter.camelCase(field)] || '';

      if (field === 'location') {
        if (!cardDataValue || cardDataValue.trim() === '') {
          return false;
        }

        const locationMatch = selectedValues.some((dest) => {
          const destNormalized = Filter.normalizeValue(dest);
          const cardNormalized = Filter.normalizeValue(cardDataValue);
          return cardNormalized.includes(destNormalized);
        });

        if (!locationMatch) return false;
      } else if (field === 'attributes') {
        const cardValues = cardDataValue.split(',').filter(Boolean);

        if (cardValues.length === 0) {
          return false;
        }

        const hasAllValues = selectedValues.every((val) => cardValues.includes(val));
        if (!hasAllValues) return false;
      } else {
        const cardValues = cardDataValue.split(',').filter(Boolean);

        if (cardValues.length === 0) {
          return false;
        }

        const hasAllValues = selectedValues.every((val) => cardValues.includes(val));
        if (!hasAllValues) return false;
      }
    }

    return true;
  }

  static camelCase(str) {
    return str.replace(/[-_]([a-z])/g, (g) => g[1].toUpperCase());
  }

  static normalizeValue(str) {
    if (!str) return '';
    return str
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ')
      .replace(/\//g, ' ')
      .replace(/,/g, ' ')
      .replace(/-/g, ' ')
      .replace(/\s+/g, '-')
      .trim();
  }

  hideUnusedCheckboxes() {
    const groups = document.querySelectorAll('[data-filter-type="checkbox"][data-filter-field]');
    if (!groups.length) return;

    const cards = Array.from(this.elements.cards);

    groups.forEach((group) => {
      const { filterField: field } = group.dataset;
      const inputs = group.querySelectorAll('input[type="checkbox"]');

      inputs.forEach((input) => {
        const { value } = input;
        const hasMatch = cards.some((card) => Filter.matchesFilters(card, {
          number: {},
          range: {},
          checkbox: { [field]: [value] },
        }));

        if (!hasMatch) {
          const row = input.closest('.brand-venues__destination-row') || input.closest('label');
          if (row) {
            row.style.display = 'none';
            row.setAttribute('hidden', '');
          }
        }
      });
    });
  }

  updateQuantity(count) {
    if (this.elements.quantityDisplay) {
      this.elements.quantityDisplay.textContent = count;
    }
  }

  toggleNoResults(count) {
    const noResults = this.elements.cardsContainer?.querySelector('.brand-venues__no-results');
    if (!noResults) return;

    if (count === 0) {
      noResults.style.display = '';
      noResults.removeAttribute('hidden');
    } else {
      noResults.style.display = 'none';
      noResults.setAttribute('hidden', '');
    }
  }

  restoreAppliedFilters() {
    if (!this.appliedFilters) {
      this.elements.form.reset();
      return;
    }

    if (Object.keys(this.appliedFilters.number).length === 0) {
      const allNumberInputs = this.elements.form.querySelectorAll('[data-filter-field]');
      allNumberInputs.forEach((input) => {
        // eslint-disable-next-line no-param-reassign
        input.value = '';
      });
    } else {
      Object.entries(this.appliedFilters.number || {}).forEach(([field, value]) => {
        const input = this.elements.form.querySelector(`input[data-filter-field="${field}"]`);
        if (input) {
          input.value = value;
        }
      });
    }

    Object.entries(this.appliedFilters.range || {}).forEach(([field, range]) => {
      const minInput = this.elements.form.querySelector(`input[data-filter-field="${field}"][data-range-type="min"]`);
      const maxInput = this.elements.form.querySelector(`input[data-filter-field="${field}"][data-range-type="max"]`);

      if (minInput) {
        const minValue = range.min !== null ? range.min : range.originalMin;
        minInput.value = minValue;
        minInput.dispatchEvent(new Event('input', { bubbles: true }));
        minInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (maxInput) {
        const maxValue = range.max !== null ? range.max : range.originalMax;
        maxInput.value = maxValue;
        maxInput.dispatchEvent(new Event('input', { bubbles: true }));
        maxInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    if (Object.keys(this.appliedFilters.checkbox).length === 0) {
      const allCheckboxGroups = this.elements.form.querySelectorAll('[data-filter-field]');
      allCheckboxGroups.forEach((group) => {
        const checkboxes = group.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach((checkbox) => {
          // eslint-disable-next-line no-param-reassign
          checkbox.checked = false;
        });
      });
    } else {
      Object.entries(this.appliedFilters.checkbox).forEach(([field, values]) => {
        const group = this.elements.form.querySelector(`[data-filter-field="${field}"]`);
        if (group) {
          const checkboxes = group.querySelectorAll('input[type="checkbox"]');
          checkboxes.forEach((checkbox) => {
            // eslint-disable-next-line no-param-reassign
            checkbox.checked = false;
          });

          values.forEach((value) => {
            const checkbox = group.querySelector(`input[type="checkbox"][value="${value}"]`);
            if (checkbox) {
              checkbox.checked = true;
              checkbox.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }
      });
    }
  }
}

export default Filter;
